/**
 * Generated by orval v8.22.0 🍺
 * Do not edit manually.
 * GetDocument.Insider
 * OpenAPI spec version: 1.0.0
 */
import {
    HttpClient,
    HttpHeaders,
    HttpResponse as AngularHttpResponse,
} from '@angular/common/http';
import type { HttpContext, HttpEvent, HttpParams } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import type {
    CreateBillingStripeWebhookBodyOne,
    CreateBuildingCheckoutSessionRequest,
    CreateBuildingCheckoutSessionResponse,
    CreateOrganisationBillingPortalSessionResponse,
    GetBuildingCheckoutPricingPreviewResponse,
    ListOrganisationBillingEntitlementsParams,
    ListOrganisationBillingEntitlementsResponse,
} from '../core-models';

interface HttpClientOptions {
    readonly headers?: HttpHeaders | Record<string, string | string[]>;
    readonly context?: HttpContext;
    readonly params?:
        | HttpParams
        | Record<
              string,
              string | number | boolean | Array<string | number | boolean>
          >;
    readonly reportProgress?: boolean;
    readonly withCredentials?: boolean;
    readonly credentials?: RequestCredentials;
    readonly keepalive?: boolean;
    readonly priority?: RequestPriority;
    readonly cache?: RequestCache;
    readonly mode?: RequestMode;
    readonly redirect?: RequestRedirect;
    readonly referrer?: string;
    readonly integrity?: string;
    readonly referrerPolicy?: ReferrerPolicy;
    readonly transferCache?: { includeHeaders?: string[] } | boolean;
    readonly timeout?: number;
}

type HttpClientBodyOptions = HttpClientOptions & {
    readonly observe?: 'body';
};

type HttpClientEventOptions = HttpClientOptions & {
    readonly observe: 'events';
};

type HttpClientResponseOptions = HttpClientOptions & {
    readonly observe: 'response';
};

type HttpClientObserveOptions = HttpClientOptions & {
    readonly observe?: 'body' | 'events' | 'response';
};

type AngularHttpParamValue =
    | string
    | number
    | boolean
    | Array<string | number | boolean>;
type AngularHttpParamValueWithNullable = AngularHttpParamValue | null;

function filterParams(
    params: Record<string, unknown>,
    requiredNullableKeys?: ReadonlySet<string>,
    preserveRequiredNullables?: false,
    passthroughKeys?: undefined
): Record<string, AngularHttpParamValue>;
function filterParams(
    params: Record<string, unknown>,
    requiredNullableKeys: ReadonlySet<string> | undefined,
    preserveRequiredNullables: true,
    passthroughKeys?: undefined
): Record<string, AngularHttpParamValueWithNullable>;
function filterParams(
    params: Record<string, unknown>,
    requiredNullableKeys: ReadonlySet<string> | undefined,
    preserveRequiredNullables: boolean | undefined,
    passthroughKeys: ReadonlySet<string>
): Record<string, unknown>;
function filterParams(
    params: Record<string, unknown>,
    requiredNullableKeys: ReadonlySet<string> = new Set(),
    preserveRequiredNullables = false,
    passthroughKeys: ReadonlySet<string> = new Set()
): Record<string, unknown> {
    const filteredParams: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
        if (passthroughKeys.has(key)) {
            if (value !== undefined) {
                filteredParams[key] = value;
            }
            continue;
        }
        if (Array.isArray(value)) {
            const filtered = value.filter(
                (item) =>
                    item != null &&
                    (typeof item === 'string' ||
                        typeof item === 'number' ||
                        typeof item === 'boolean')
            ) as Array<string | number | boolean>;
            if (filtered.length) {
                filteredParams[key] = filtered;
            }
        } else if (value === null && requiredNullableKeys.has(key)) {
            // With a paramsSerializer (preserveRequiredNullables) the literal null
            // is passed through for it to consume; without one, emit an empty
            // string so the required key still reaches the wire as `?key=`
            // instead of being silently dropped. See #3712.
            filteredParams[key] = preserveRequiredNullables ? null : '';
        } else if (
            value != null &&
            (typeof value === 'string' ||
                typeof value === 'number' ||
                typeof value === 'boolean')
        ) {
            filteredParams[key] = value;
        }
    }
    return filteredParams;
}

@Injectable({ providedIn: 'root' })
export class BillingApiService {
    private readonly http = inject(HttpClient);
    /**
     * Returns monthly and yearly dynamic pricing offers for a draft building, reusing any pending-entitlement pricing snapshot when available.
     * @summary Get building checkout pricing preview
     */
    getBillingBuildingCheckoutPricingPreview<
        TData = GetBuildingCheckoutPricingPreviewResponse,
    >(buildingId: string, options?: HttpClientBodyOptions): Observable<TData>;
    getBillingBuildingCheckoutPricingPreview<
        TData = GetBuildingCheckoutPricingPreviewResponse,
    >(
        buildingId: string,
        options?: HttpClientEventOptions
    ): Observable<HttpEvent<TData>>;
    getBillingBuildingCheckoutPricingPreview<
        TData = GetBuildingCheckoutPricingPreviewResponse,
    >(
        buildingId: string,
        options?: HttpClientResponseOptions
    ): Observable<AngularHttpResponse<TData>>;
    getBillingBuildingCheckoutPricingPreview<
        TData = GetBuildingCheckoutPricingPreviewResponse,
    >(
        buildingId: string,
        options?: HttpClientObserveOptions
    ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
        if (options?.observe === 'events') {
            return this.http.get<TData>(
                `/@core/api/billing/buildings/${buildingId}/checkout-pricing-preview`,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'events',
                }
            );
        }

        if (options?.observe === 'response') {
            return this.http.get<TData>(
                `/@core/api/billing/buildings/${buildingId}/checkout-pricing-preview`,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'response',
                }
            );
        }

        return this.http.get<TData>(
            `/@core/api/billing/buildings/${buildingId}/checkout-pricing-preview`,
            {
                ...(options as Omit<NonNullable<typeof options>, 'observe'>),
                observe: 'body',
            }
        );
    }
    /**
     * Creates or reuses a pending building entitlement and returns a fresh Stripe Checkout URL.
     * @summary Create building checkout session
     */
    createBillingBuildingCheckoutSession<
        TData = CreateBuildingCheckoutSessionResponse,
    >(
        buildingId: string,
        createBuildingCheckoutSessionRequest: CreateBuildingCheckoutSessionRequest,
        options?: HttpClientBodyOptions
    ): Observable<TData>;
    createBillingBuildingCheckoutSession<
        TData = CreateBuildingCheckoutSessionResponse,
    >(
        buildingId: string,
        createBuildingCheckoutSessionRequest: CreateBuildingCheckoutSessionRequest,
        options?: HttpClientEventOptions
    ): Observable<HttpEvent<TData>>;
    createBillingBuildingCheckoutSession<
        TData = CreateBuildingCheckoutSessionResponse,
    >(
        buildingId: string,
        createBuildingCheckoutSessionRequest: CreateBuildingCheckoutSessionRequest,
        options?: HttpClientResponseOptions
    ): Observable<AngularHttpResponse<TData>>;
    createBillingBuildingCheckoutSession<
        TData = CreateBuildingCheckoutSessionResponse,
    >(
        buildingId: string,
        createBuildingCheckoutSessionRequest: CreateBuildingCheckoutSessionRequest,
        options?: HttpClientObserveOptions
    ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
        if (options?.observe === 'events') {
            return this.http.post<TData>(
                `/@core/api/billing/buildings/${buildingId}/checkout-session`,
                createBuildingCheckoutSessionRequest,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'events',
                }
            );
        }

        if (options?.observe === 'response') {
            return this.http.post<TData>(
                `/@core/api/billing/buildings/${buildingId}/checkout-session`,
                createBuildingCheckoutSessionRequest,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'response',
                }
            );
        }

        return this.http.post<TData>(
            `/@core/api/billing/buildings/${buildingId}/checkout-session`,
            createBuildingCheckoutSessionRequest,
            {
                ...(options as Omit<NonNullable<typeof options>, 'observe'>),
                observe: 'body',
            }
        );
    }
    /**
     * Verifies and processes Stripe billing webhooks for building entitlements.
     * @summary Handle Stripe webhook
     */
    createBillingStripeWebhook<TData = void>(
        createBillingStripeWebhookBody:
            | CreateBillingStripeWebhookBodyOne
            | Blob,
        options?: HttpClientBodyOptions
    ): Observable<TData>;
    createBillingStripeWebhook<TData = void>(
        createBillingStripeWebhookBody:
            | CreateBillingStripeWebhookBodyOne
            | Blob,
        options?: HttpClientEventOptions
    ): Observable<HttpEvent<TData>>;
    createBillingStripeWebhook<TData = void>(
        createBillingStripeWebhookBody:
            | CreateBillingStripeWebhookBodyOne
            | Blob,
        options?: HttpClientResponseOptions
    ): Observable<AngularHttpResponse<TData>>;
    createBillingStripeWebhook<TData = void>(
        createBillingStripeWebhookBody:
            | CreateBillingStripeWebhookBodyOne
            | Blob,
        options?: HttpClientObserveOptions
    ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
        if (options?.observe === 'events') {
            return this.http.post<TData>(
                `/@core/api/billing/stripe/webhook`,
                createBillingStripeWebhookBody,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'events',
                }
            );
        }

        if (options?.observe === 'response') {
            return this.http.post<TData>(
                `/@core/api/billing/stripe/webhook`,
                createBillingStripeWebhookBody,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'response',
                }
            );
        }

        return this.http.post<TData>(
            `/@core/api/billing/stripe/webhook`,
            createBillingStripeWebhookBody,
            {
                ...(options as Omit<NonNullable<typeof options>, 'observe'>),
                observe: 'body',
            }
        );
    }
    /**
     * Lists owned buildings with billing entitlement history for an organisation billing dashboard, including payment-required and paid states.
     * @summary List organisation billing entitlements
     */
    listOrganisationBillingEntitlements<
        TData = ListOrganisationBillingEntitlementsResponse,
    >(
        organisationId: string,
        params: ListOrganisationBillingEntitlementsParams,
        options?: HttpClientBodyOptions
    ): Observable<TData>;
    listOrganisationBillingEntitlements<
        TData = ListOrganisationBillingEntitlementsResponse,
    >(
        organisationId: string,
        params: ListOrganisationBillingEntitlementsParams,
        options?: HttpClientEventOptions
    ): Observable<HttpEvent<TData>>;
    listOrganisationBillingEntitlements<
        TData = ListOrganisationBillingEntitlementsResponse,
    >(
        organisationId: string,
        params: ListOrganisationBillingEntitlementsParams,
        options?: HttpClientResponseOptions
    ): Observable<AngularHttpResponse<TData>>;
    listOrganisationBillingEntitlements<
        TData = ListOrganisationBillingEntitlementsResponse,
    >(
        organisationId: string,
        params: ListOrganisationBillingEntitlementsParams,
        options?: HttpClientObserveOptions
    ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
        const filteredParams = filterParams(
            { ...params, ...options?.params },
            new Set<string>([])
        );

        if (options?.observe === 'events') {
            return this.http.get<TData>(
                `/@core/api/organisations/${organisationId}/billing/entitlements`,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'events',
                    params: filteredParams,
                }
            );
        }

        if (options?.observe === 'response') {
            return this.http.get<TData>(
                `/@core/api/organisations/${organisationId}/billing/entitlements`,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'response',
                    params: filteredParams,
                }
            );
        }

        return this.http.get<TData>(
            `/@core/api/organisations/${organisationId}/billing/entitlements`,
            {
                ...(options as Omit<NonNullable<typeof options>, 'observe'>),
                observe: 'body',
                params: filteredParams,
            }
        );
    }
    /**
     * Creates a Stripe Billing Portal session for an organisation with billing customer history.
     * @summary Create organisation billing portal session
     */
    createOrganisationBillingPortalSession<
        TData = CreateOrganisationBillingPortalSessionResponse,
    >(
        organisationId: string,
        options?: HttpClientBodyOptions
    ): Observable<TData>;
    createOrganisationBillingPortalSession<
        TData = CreateOrganisationBillingPortalSessionResponse,
    >(
        organisationId: string,
        options?: HttpClientEventOptions
    ): Observable<HttpEvent<TData>>;
    createOrganisationBillingPortalSession<
        TData = CreateOrganisationBillingPortalSessionResponse,
    >(
        organisationId: string,
        options?: HttpClientResponseOptions
    ): Observable<AngularHttpResponse<TData>>;
    createOrganisationBillingPortalSession<
        TData = CreateOrganisationBillingPortalSessionResponse,
    >(
        organisationId: string,
        options?: HttpClientObserveOptions
    ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
        if (options?.observe === 'events') {
            return this.http.post<TData>(
                `/@core/api/organisations/${organisationId}/billing/portal-session`,
                undefined,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'events',
                }
            );
        }

        if (options?.observe === 'response') {
            return this.http.post<TData>(
                `/@core/api/organisations/${organisationId}/billing/portal-session`,
                undefined,
                {
                    ...(options as Omit<
                        NonNullable<typeof options>,
                        'observe'
                    >),
                    observe: 'response',
                }
            );
        }

        return this.http.post<TData>(
            `/@core/api/organisations/${organisationId}/billing/portal-session`,
            undefined,
            {
                ...(options as Omit<NonNullable<typeof options>, 'observe'>),
                observe: 'body',
            }
        );
    }
}
